(() => {
  const initialized = new WeakSet();
  const DEFAULT_SORT = 'date_desc';
  const SORT_VALUES = new Set(['date_desc', 'relevance', 'name_asc', 'price_asc', 'price_desc', 'date_asc', 'edhrec_rank_asc']);
  const SHOPIFY_SORT_ALIASES = {
    'most-relevant': 'relevance',
    'title-ascending': 'name_asc',
    'price-ascending': 'price_asc',
    'price-descending': 'price_desc',
    'created-descending': 'date_desc',
    'created-ascending': 'date_asc'
  };
  const SEARCH_TIMEOUT_MS = 7000;
  const SEARCH_RETRY_DELAY_MS = 400;
  const INITIAL_SKELETON_MIN_MS = 350;
  const DEGRADED_MODE_TTL_MS = 2 * 60 * 1000;
  const GAME_LABELS = {
    pokemon_en: 'Pokémon',
    pokemon_jp: 'Pokémon JP',
    magic: 'Magic',
    riftbound: 'Riftbound',
    one_piece: 'One Piece'
  };

  function normalize(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  function money(root, value) {
    if (value === null || value === undefined) return '';
    try {
      const currency = root.dataset.currency || 'CLP';
      const formatted = new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency,
        maximumFractionDigits: 0
      }).format(value);
      return `${formatted} ${currency}`;
    } catch {
      return String(value);
    }
  }

  function compactCondition(value) {
    const conditions = {
      'near mint': 'NM',
      'lightly played': 'LP',
      'moderately played': 'MP',
      'heavily played': 'HP',
      damaged: 'DMG'
    };
    return conditions[normalize(value)] || value;
  }

  function compactFinish(value) {
    const finishes = {
      holofoil: 'Foil',
      'reverse holofoil': 'Reverse',
      'cosmos holofoil': 'Cosmos',
      'cracked ice holofoil': 'Cracked Ice'
    };
    return finishes[normalize(value)] || value;
  }

  function variantLabel(variant, compact = true) {
    const condition = compact ? compactCondition(variant.condition) : variant.condition;
    const finish = compact ? compactFinish(variant.finish) : variant.finish;
    return [variant.language, finish, condition].filter(Boolean).join(' ') || variant.sku || 'Disponible';
  }

  function text(tag, className, value) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    node.textContent = value ?? '';
    return node;
  }

  function updateCartBubble(sectionHtml) {
    const currentBubble = document.getElementById('cart-icon-bubble');
    if (!currentBubble || !sectionHtml) return false;

    const sectionDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const renderedBubble = sectionDocument.getElementById('cart-icon-bubble');
    const sectionWrapper = sectionDocument.querySelector(
      '#shopify-section-cart-icon-bubble, .shopify-section'
    );
    const bubbleHtml = renderedBubble?.innerHTML || sectionWrapper?.innerHTML || sectionDocument.body.innerHTML;
    if (!bubbleHtml) return false;

    currentBubble.innerHTML = bubbleHtml;
    return true;
  }

  function productPath(item) {
    try {
      const url = new URL(item?.productUrl || '', window.location.origin);
      if (url.pathname.startsWith('/products/')) return `${url.pathname}${url.search}`;
    } catch {
      // Fall back to the Shopify handle below.
    }
    const handle = String(item?.handle || '').trim();
    if (handle) {
      const root = window.Shopify?.routes?.root || '/';
      return `${root.replace(/\/$/, '')}/products/${encodeURIComponent(handle)}`;
    }
    return '/collections/all';
  }

  function encodeOffsetCursor(offset) {
    return window.btoa(JSON.stringify({ offset }))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  function priorityImageCount() {
    if (window.matchMedia('(max-width: 749px)').matches) return 2;
    if (window.matchMedia('(max-width: 989px)').matches) return 3;
    if (window.matchMedia('(max-width: 1199px)').matches) return 4;
    return 5;
  }

  class SinglesApiSearchController {
    constructor(root) {
      this.root = root;
      this.apiUrl = (root.dataset.apiUrl || '').replace(/\/+$/, '');
      this.defaultGame = root.dataset.defaultGame || 'magic';
      this.lockGame = root.dataset.lockGame === 'true';
      this.pageSize = Math.max(1, Number(root.dataset.pageSize) || 20);
      this.params = new URLSearchParams(window.location.search);
      this.loading = false;
      this.refreshQueued = false;
      this.degraded = false;
      this.products = [];
    }

    init() {
      this.queryInput = this.root.querySelector('[data-search-query]');
      this.gameSelect = this.root.querySelector('[data-search-game]');
      this.sortSelect = this.root.querySelector('[data-search-sort]');
      this.sortSelectDesktop = this.root.querySelector('[data-search-sort-desktop]');
      this.facetsTarget = this.root.querySelector('[data-facets]');
      this.productsTarget = this.root.querySelector('[data-products]');
      this.messageTarget = this.root.querySelector('[data-search-message]');
      this.emptyTarget = this.root.querySelector('[data-empty]');
      this.paginationTarget = this.root.querySelector('[data-pagination]');
      this.filterPanel = this.root.querySelector('[data-filter-panel]');
      this.fallbackTarget = this.root
        .closest('.singles-api-collection')
        ?.querySelector('[data-singles-native-fallback]');

      if (this.lockGame || !this.params.get('game')) this.params.set('game', this.defaultGame);
      this.params.delete('cursor');
      this.params.set('limit', String(this.pageSize));
      this.syncControlsFromParams();
      this.bindEvents();
      if (this.shouldRestoreDegradedMode()) this.enterDegradedMode();
      else this.fetchAndRender();
    }

    bindEvents() {
      let searchTimeout;
      this.queryInput?.addEventListener('input', () => {
        window.clearTimeout(searchTimeout);
        searchTimeout = window.setTimeout(() => {
          this.setParam('q', this.queryInput.value.trim());
          this.resetCursor();
          this.fetchAndRender();
        }, 350);
      });

      this.gameSelect?.addEventListener('change', () => {
        this.params = new URLSearchParams();
        this.params.set('game', this.gameSelect.value);
        this.params.set('limit', String(this.pageSize));
        this.syncControlsFromParams();
        this.fetchAndRender();
      });

      const onSortChange = (value) => {
        this.setParam('sort', value);
        this.resetCursor();
        this.syncControlsFromParams();
        this.fetchAndRender();
      };

      this.sortSelect?.addEventListener('change', () => onSortChange(this.sortSelect.value));
      this.sortSelectDesktop?.addEventListener('change', () => onSortChange(this.sortSelectDesktop.value));

      this.facetsTarget?.addEventListener('change', (event) => {
        const input = event.target.closest('[data-filter-checkbox]');
        if (!input) return;
        this.toggleFilterValue(input.name, input.value, input.checked);
        this.resetCursor();
        this.fetchAndRender();
      });

      this.facetsTarget?.addEventListener('input', (event) => {
        const input = event.target.closest('[data-facet-search]');
        if (!input) return;
        this.filterSearchableFacet(input);
      });

      this.root.querySelectorAll('[data-clear-filters]').forEach((button) => {
        button.addEventListener('click', () => this.clearFilters());
      });

      this.root.querySelector('[data-mobile-filters-open]')?.addEventListener('click', () => this.openMobileFilters());
      this.root.querySelector('[data-mobile-filters-close]')?.addEventListener('click', () => this.closeMobileFilters());
      this.root.querySelector('[data-mobile-filters-apply]')?.addEventListener('click', () => this.closeMobileFilters());
      this.filterPanel?.addEventListener('click', (event) => {
        if (event.target === this.filterPanel) this.closeMobileFilters();
      });
      this.productsTarget?.addEventListener('change', (event) => this.updateCardSelectedVariant(event));
      this.productsTarget?.addEventListener('click', (event) => this.addCardToCart(event));
      this.root.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.root.classList.contains('is-filter-open')) this.closeMobileFilters();
      });
      this.fallbackTarget?.querySelector('[data-singles-fallback-retry]')?.addEventListener('click', () => {
        this.retryAdvancedCatalog();
      });
    }

    degradedStorageKey() {
      return `singles-api-degraded:${this.apiUrl}:${this.defaultGame}`;
    }

    shouldRestoreDegradedMode() {
      if (!this.fallbackTarget) return false;
      try {
        const expiresAt = Number(window.sessionStorage.getItem(this.degradedStorageKey()) || 0);
        if (expiresAt > Date.now()) return true;
        window.sessionStorage.removeItem(this.degradedStorageKey());
      } catch {
        // The fallback still works when sessionStorage is unavailable.
      }
      return false;
    }

    rememberDegradedMode() {
      try {
        window.sessionStorage.setItem(
          this.degradedStorageKey(),
          String(Date.now() + DEGRADED_MODE_TTL_MS)
        );
      } catch {
        // The fallback does not depend on browser storage.
      }
    }

    forgetDegradedMode() {
      try {
        window.sessionStorage.removeItem(this.degradedStorageKey());
      } catch {
        // Nothing else is required.
      }
    }

    enterDegradedMode() {
      const hasMagicConstraints = [...this.params.keys()].some(key => ['magic_artist', 'magic_format', 'commander_staple',
        'filter.p.m.simple_tcg.magic_artist', 'filter.p.m.simple_tcg.magic_playable_formats',
        'filter.p.m.simple_tcg.magic_commander_staple'].includes(key)) || this.params.get('sort') === 'edhrec_rank_asc';
      if (hasMagicConstraints) {
        this.products = [];
        this.productsTarget?.replaceChildren();
        this.updateUrl();
        this.syncClearFilterButtons();
        this.setMessage('El catálogo avanzado no está disponible. Se conservan tus filtros de Magic; el catálogo básico no puede garantizar estos filtros ni el orden EDHREC. Reintenta en unos minutos.', 'error');
        if (this.messageTarget) {
          const retry = document.createElement('button');
          retry.type = 'button';
          retry.className = 'button button--tertiary';
          retry.textContent = 'Reintentar';
          retry.addEventListener('click', () => { this.forgetDegradedMode(); this.fetchAndRender(); });
          this.messageTarget.append(retry);
        }
        return;
      }
      if (!this.fallbackTarget) {
        this.setMessage(
          'Los filtros avanzados no están disponibles temporalmente. Intenta nuevamente en unos minutos.',
          'error'
        );
        return;
      }

      this.degraded = true;
      this.refreshQueued = false;
      this.queuedUiState = null;
      this.closeMobileFilters();
      this.rememberDegradedMode();
      this.root.hidden = true;
      this.fallbackTarget.hidden = false;
    }

    retryAdvancedCatalog() {
      if (this.loading) return;
      this.degraded = false;
      this.forgetDegradedMode();
      if (this.fallbackTarget) this.fallbackTarget.hidden = true;
      this.root.hidden = false;
      this.products = [];
      this.fetchAndRender();
    }

    clearFilters() {
      const game = this.params.get('game') || this.defaultGame;
      this.params = new URLSearchParams();
      this.params.set('game', game);
      this.params.set('limit', String(this.pageSize));
      this.syncControlsFromParams();
      this.fetchAndRender();
    }

    syncControlsFromParams() {
      const game = this.params.get('game') || this.defaultGame;
      if (this.gameSelect) this.gameSelect.value = game;
      if (this.queryInput) this.queryInput.value = this.params.get('q') || '';
      const requestedSort = this.params.get('sort') || SHOPIFY_SORT_ALIASES[this.params.get('sort_by')];
      const sort = SORT_VALUES.has(requestedSort) ? requestedSort : DEFAULT_SORT;
      // Keep the selected order explicit in API requests, URLs and pagination.
      this.params.set('sort', sort);
      this.params.delete('sort_by');
      if (this.sortSelect) this.sortSelect.value = sort;
      if (this.sortSelectDesktop) this.sortSelectDesktop.value = sort;
      this.syncClearFilterButtons();
    }

    syncClearFilterButtons() {
      const hasActiveFilters = [...this.params.entries()].some(([key, value]) => (
        key !== 'game' && key !== 'limit' && !(key === 'sort' && value === DEFAULT_SORT)
      ));
      this.root.querySelectorAll('[data-clear-filters]').forEach((button) => {
        button.disabled = !hasActiveFilters;
      });
    }

    captureUiState() {
      const dialog = this.filterPanel?.querySelector('.singles-api-search__filters-dialog');
      const activeElement = document.activeElement;
      const activeFilter = activeElement?.matches?.('[data-filter-checkbox]')
        ? { name: activeElement.name, value: activeElement.value }
        : null;
      const searchableLists = [...(this.facetsTarget?.querySelectorAll('[data-searchable-list]') || [])]
        .map((list) => ({
          facetId: list.closest('.singles-api-search__facet')?.dataset.facetId || '',
          scrollTop: list.scrollTop
        }));

      return {
        windowScrollTop: window.scrollY,
        catalogTop: this.root.getBoundingClientRect().top + window.scrollY,
        dialogScrollTop: dialog?.scrollTop || 0,
        searchableLists,
        activeFilter
      };
    }

    restoreUiState(state) {
      if (!state) return;
      const restoreScroll = () => {
        const maximumScrollTop = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
        const windowScrollTop = state.windowScrollTop <= maximumScrollTop
          ? state.windowScrollTop
          : Math.min(state.catalogTop, maximumScrollTop);
        window.scrollTo(0, windowScrollTop);
        const dialog = this.filterPanel?.querySelector('.singles-api-search__filters-dialog');
        if (dialog) dialog.scrollTop = state.dialogScrollTop;
        state.searchableLists.forEach(({ facetId, scrollTop }) => {
          const facet = [...(this.facetsTarget?.querySelectorAll('.singles-api-search__facet') || [])]
            .find((candidate) => candidate.dataset.facetId === facetId);
          const list = facet?.querySelector('[data-searchable-list]');
          if (list) list.scrollTop = scrollTop;
        });
      };

      restoreScroll();
      if (state.activeFilter) {
        const activeFilter = [...(this.facetsTarget?.querySelectorAll('[data-filter-checkbox]') || [])]
          .find((input) => input.name === state.activeFilter.name && input.value === state.activeFilter.value);
        if (activeFilter) {
          try {
            activeFilter.focus({ preventScroll: true });
          } catch {
            activeFilter.focus();
          }
        }
      }
      restoreScroll();
      window.requestAnimationFrame(restoreScroll);
    }

    setParam(key, value) {
      if (value) this.params.set(key, value);
      else this.params.delete(key);
    }

    resetCursor() {
      this.params.delete('cursor');
      this.params.delete('page');
    }

    toggleFilterValue(paramName, value, checked) {
      const values = this.params.getAll(paramName).filter((current) => current !== value);
      this.params.delete(paramName);
      values.forEach((current) => this.params.append(paramName, current));
      if (checked) this.params.append(paramName, value);
    }

    async requestSearch(url) {
      let lastError;
      for (let attempt = 0; attempt < 2; attempt += 1) {
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), SEARCH_TIMEOUT_MS);
        try {
          const response = await fetch(url, {
            headers: { accept: 'application/json' },
            signal: controller.signal
          });
          const payload = await response.json().catch(() => ({}));
          if (!response.ok) {
            throw new Error(payload.message || `El servicio de filtros respondió con estado ${response.status}.`);
          }
          return payload;
        } catch (error) {
          lastError = error;
          if (attempt === 0) {
            await new Promise((resolve) => window.setTimeout(resolve, SEARCH_RETRY_DELAY_MS));
          }
        } finally {
          window.clearTimeout(timeout);
        }
      }

      if (lastError?.name === 'AbortError') {
        throw new Error('El servicio de filtros tardó demasiado en responder.');
      }
      throw lastError || new Error('No pudimos comunicarnos con el servicio de filtros.');
    }

    async fetchAndRender(uiState = this.captureUiState()) {
      if (!this.apiUrl) {
        this.setMessage('Falta configurar la URL publica de Filtros Singles CC en esta seccion.', 'error');
        return;
      }
      if (this.degraded) return;
      if (this.loading) {
        this.refreshQueued = true;
        this.queuedUiState = uiState;
        return;
      }
      const initialLoad = this.products.length === 0;
      const initialLoadingStartedAt = initialLoad ? Date.now() : 0;
      this.loading = true;
      this.root.classList.add('is-loading');
      this.root.classList.toggle('is-initial-loading', initialLoad);
      this.productsTarget?.setAttribute('aria-busy', 'true');
      this.setMessage('', '');
      try {
        const page = this.currentPage();
        const apiParams = new URLSearchParams(this.params);
        apiParams.delete('page');
        apiParams.delete('cursor');
        apiParams.set('limit', String(this.pageSize));
        apiParams.set('filter.v.availability', '1');
        if (page > 1) apiParams.set('cursor', encodeOffsetCursor((page - 1) * this.pageSize));
        const query = apiParams.toString();
        const searchPayload = await this.requestSearch(`${this.apiUrl}/api/search?${query}`);
        if (this.refreshQueued) return;
        if (initialLoad) {
          const remainingSkeletonTime = INITIAL_SKELETON_MIN_MS - (Date.now() - initialLoadingStartedAt);
          if (remainingSkeletonTime > 0) {
            await new Promise((resolve) => window.setTimeout(resolve, remainingSkeletonTime));
          }
        }

        this.forgetDegradedMode();
        this.products = searchPayload.items || [];
        this.renderFacets(searchPayload.filters || []);
        this.renderProducts(this.products);
        this.renderCounts(searchPayload);
        this.renderPagination(searchPayload.totalCount, page);
        this.updateUrl();
        this.syncClearFilterButtons();
        this.restoreUiState(uiState);
        this.setMessage('', '');
      } catch (error) {
        console.warn('El catálogo avanzado no está disponible; activando el modo básico.', error);
        this.enterDegradedMode();
      } finally {
        this.loading = false;
        this.root.classList.remove('is-loading');
        this.root.classList.remove('is-initial-loading');
        this.productsTarget?.setAttribute('aria-busy', 'false');
        if (!this.degraded && this.refreshQueued) {
          this.refreshQueued = false;
          const queuedUiState = this.queuedUiState;
          this.queuedUiState = null;
          this.fetchAndRender(queuedUiState);
        }
      }
    }

    renderCounts(payload) {
      const totalCount = Number.isFinite(payload.totalCount) ? payload.totalCount : this.products.length;
      const totalText = `${totalCount} producto${totalCount === 1 ? '' : 's'}`;
      this.root.querySelectorAll('[data-product-count], [data-product-count-desktop]').forEach((node) => {
        node.textContent = totalText;
      });
      if (this.emptyTarget) this.emptyTarget.hidden = this.products.length > 0;
    }

    currentPage() {
      const page = Number(this.params.get('page'));
      return Number.isSafeInteger(page) && page > 0 ? page : 1;
    }

    pageHref(page) {
      const params = new URLSearchParams(this.params);
      params.delete('cursor');
      params.set('limit', String(this.pageSize));
      if (page > 1) params.set('page', String(page));
      else params.delete('page');
      const query = params.toString();
      return `${window.location.pathname}${query ? `?${query}` : ''}`;
    }

    renderPagination(totalCount, currentPage) {
      if (!this.paginationTarget) return;
      const totalPages = Math.max(1, Math.ceil((Number(totalCount) || 0) / this.pageSize));
      if (totalPages <= 1) {
        this.paginationTarget.replaceChildren();
        this.paginationTarget.hidden = true;
        return;
      }

      const nav = document.createElement('nav');
      nav.className = 'pagination';
      nav.setAttribute('role', 'navigation');
      nav.setAttribute('aria-label', 'Paginación');
      const list = text('ul', 'pagination__list list-unstyled', '');
      list.setAttribute('role', 'list');

      if (currentPage > 1) list.append(this.renderPageLink(currentPage - 1, '‹', 'Página anterior', true));

      const visiblePages = [];
      for (let page = 1; page <= totalPages; page += 1) {
        if (page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1) visiblePages.push(page);
      }
      visiblePages.forEach((page, index) => {
        if (index > 0 && page - visiblePages[index - 1] > 1) {
          const ellipsisItem = text('li', '', '');
          ellipsisItem.append(text('span', 'pagination__item', '…'));
          list.append(ellipsisItem);
        }
        if (page === currentPage) {
          const item = text('li', '', '');
          const current = text('a', 'pagination__item pagination__item--current light', String(page));
          current.setAttribute('role', 'link');
          current.setAttribute('aria-disabled', 'true');
          current.setAttribute('aria-current', 'page');
          current.setAttribute('aria-label', `Página ${page}`);
          item.append(current);
          list.append(item);
        } else {
          list.append(this.renderPageLink(page, String(page), `Página ${page}`));
        }
      });

      if (currentPage < totalPages) list.append(this.renderPageLink(currentPage + 1, '›', 'Página siguiente', true));
      nav.append(list);
      this.paginationTarget.replaceChildren(nav);
      this.paginationTarget.hidden = false;
    }

    renderPageLink(page, label, ariaLabel, arrow = false) {
      const item = text('li', '', '');
      const link = text('a', `pagination__item link${arrow ? ' singles-api-search__page-arrow' : ''}`, label);
      link.href = this.pageHref(page);
      link.setAttribute('aria-label', ariaLabel);
      item.append(link);
      return item;
    }

    renderFacets(filters) {
      if (!this.facetsTarget) return;
      const openFacets = new Set(
        [...this.facetsTarget.querySelectorAll('.singles-api-search__facet[open]')]
          .map((facet) => facet.dataset.facetId)
          .filter(Boolean)
      );
      const visibleFilters = filters.filter((filter) => (
        !['commander_staple', 'magic_commander_staple'].includes(filter.id)
        && filter.paramName !== 'filter.p.m.simple_tcg.magic_commander_staple'
      ));
      this.facetsTarget.replaceChildren(...visibleFilters.map((filter, index) => this.renderFacet(filter, index, openFacets)));
    }

    renderFacet(filter, index, openFacets = new Set()) {
      const details = document.createElement('details');
      details.className = 'singles-api-search__facet';
      details.dataset.facetId = filter.id || filter.paramName || String(index);
      if (openFacets.has(details.dataset.facetId)) details.open = true;

      const isEdition = this.isEditionFilter(filter);
      const isSearchable = isEdition || this.isArtistFilter(filter);
      if (isSearchable) details.classList.add('singles-api-search__facet--searchable');

      const summary = text('summary', 'singles-api-search__facet-summary', '');
      const selectedCount = filter.values?.filter((value) => value.active).length || 0;
      summary.append(text('span', '', this.facetLabel(filter)));
      if (selectedCount > 0) summary.append(text('span', 'singles-api-search__facet-selected', `(${selectedCount})`));
      summary.append(text('span', 'singles-api-search__facet-caret', '⌄'));
      details.append(summary);

      const panel = text('div', 'singles-api-search__facet-panel', '');
      if (isSearchable) {
        panel.append(this.renderFacetSearch(filter));
      }

      const list = text('ul', 'singles-api-search__facet-list list-unstyled', '');
      if (isSearchable) list.dataset.searchableList = 'true';
      const values = [...(filter.values || [])];
      if (isSearchable) {
        values.sort((left, right) => String(left.label || left.value || '').localeCompare(
          String(right.label || right.value || ''),
          'es',
          { sensitivity: 'base', numeric: true }
        ));
      }
      values.forEach((value) => list.append(this.renderFacetValue(value)));
      panel.append(list);
      const empty = text(
        'p',
        'singles-api-search__facet-empty hidden',
        `No encontramos ${isEdition ? 'ediciones' : 'artistas'} con esa búsqueda.`
      );
      empty.dataset.facetEmpty = 'true';
      if (isSearchable) panel.append(empty);
      details.append(panel);
      return details;
    }

    renderFacetSearch(filter) {
      const wrapper = text('div', 'singles-api-search__edition-search', '');
      wrapper.append(text('div', 'singles-api-search__edition-label', `Seleccionar ${this.facetLabel(filter)}`));
      const input = document.createElement('input');
      input.type = 'search';
      input.placeholder = 'Buscar...';
      input.autocomplete = 'off';
      input.dataset.facetSearch = 'true';
      wrapper.append(input);
      return wrapper;
    }

    renderFacetValue(value) {
      const item = text('li', 'singles-api-search__facet-item', '');
      item.dataset.filterText = value.label || value.value || '';
      const label = text('label', 'singles-api-search__checkbox', '');
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.name = value.paramName;
      input.value = value.value;
      input.checked = Boolean(value.active);
      input.disabled = value.count === 0 && !value.active;
      input.dataset.filterCheckbox = 'true';
      label.append(input);
      label.append(text('span', 'singles-api-search__checkbox-box', ''));
      label.append(text('span', 'singles-api-search__checkbox-label', `${value.label} (${value.count})`));
      item.append(label);
      return item;
    }

    renderProducts(items) {
      if (!this.productsTarget) return;
      const eagerImages = priorityImageCount();
      this.productsTarget.replaceChildren(
        ...items.map((item, index) => this.renderProductCard(item, index < eagerImages))
      );
    }

    renderProductCard(item, prioritizeImage = false) {
      const card = text('article', 'singles-api-search__card card-wrapper product-card-wrapper underline-links-hover product-card-wrapper--variant-quick-add', '');
      const href = productPath(item);
      if (item.imageUrl) {
        const imageLink = document.createElement('a');
        imageLink.href = href;
        imageLink.className = 'singles-api-search__card-image-link';
        const image = document.createElement('img');
        image.src = item.imageUrl;
        image.alt = item.title;
        image.loading = prioritizeImage ? 'eager' : 'lazy';
        image.decoding = 'async';
        if (prioritizeImage) image.fetchPriority = 'high';
        imageLink.append(image);
        card.append(imageLink);
      }

      const information = text('div', 'card__information singles-api-search__card-information', '');
      const title = text('h3', 'singles-api-search__card-title card__heading h5', '');
      const link = text('a', '', item.title);
      link.href = href;
      link.className = 'full-unstyled-link';
      link.title = item.title;
      title.append(link);
      information.append(title);

      const variants = (item.variants || []).filter((variant) =>
        variant?.available === true && Number(variant?.stock || 0) > 0
      );
      const selectedVariant = variants[0];
      const price = text('div', 'singles-api-search__card-price price card-variant-quick-add-price', '');
      price.setAttribute('aria-live', 'polite');
      const priceValue = text('span', 'price-item price-item--regular', money(this.root, selectedVariant?.price ?? item.minPrice));
      priceValue.dataset.cardVariantPriceValue = 'true';
      price.append(priceValue);
      const cardInformation = text('div', 'card-information', '');
      cardInformation.append(price);
      information.append(cardInformation);
      card.append(information);

      if (variants.length > 0) {
        const quickAdd = document.createElement('div');
        quickAdd.className = 'card-variant-quick-add quick-add no-js-hidden';
        const form = text('div', 'card-variant-quick-add__form', '');
        const selectWrapper = text('div', 'select card-variant-quick-add__select', '');
        const select = document.createElement('select');
        select.className = 'select__select singles-api-search__variant-select';
        select.name = 'id';
        select.dataset.variantSelect = 'true';
        select.setAttribute('aria-label', `Versión de ${item.title}`);
        variants.forEach((variant) => {
          const option = document.createElement('option');
          option.value = variant.variantId;
          option.dataset.rawPrice = variant.price ?? '';
          option.dataset.variantTitle = variantLabel(variant, false);
          option.textContent = variantLabel(variant);
          option.title = variantLabel(variant, false);
          select.append(option);
        });
        const caret = text('span', 'singles-api-search__select-caret', '⌄');
        selectWrapper.append(select, caret);
        form.append(selectWrapper);

        const button = text('button', 'singles-api-search__add quick-add__submit button button--full-width button--secondary card-variant-quick-add__submit', 'Agregar al carrito');
        button.type = 'button';
        button.dataset.addVariant = selectedVariant.variantId;
        form.append(button);
        quickAdd.append(form);
        card.append(quickAdd);
        this.updateCardHolographicEffect(card, select.options[select.selectedIndex]);
      } else {
        card.append(text('p', 'singles-api-search__sold-out', 'Sin stock disponible'));
      }

      return card;
    }

    updateCardSelectedVariant(event) {
      const select = event.target.closest('[data-variant-select]');
      if (!select) return;
      const card = select.closest('.singles-api-search__card');
      const button = card?.querySelector('[data-add-variant]');
      if (button) button.dataset.addVariant = select.value;
      const selected = select.options[select.selectedIndex];
      const price = card?.querySelector('[data-card-variant-price-value]');
      if (price && selected?.dataset.rawPrice !== undefined) {
        price.textContent = selected.dataset.rawPrice === '' ? '' : money(this.root, Number(selected.dataset.rawPrice));
      }
      this.updateCardHolographicEffect(card, selected);
    }

    updateCardHolographicEffect(card, selectedOption) {
      const isHolographic = window.HolographicEffect?.matches(selectedOption?.dataset.variantTitle);
      card?.classList.toggle('is-holographic', Boolean(isHolographic));
    }

    async addCardToCart(event) {
      const button = event.target.closest('[data-add-variant]');
      if (!button) return;
      const id = button.dataset.addVariant;
      if (!id) return;
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const sections = typeof cart?.getSectionsToRender === 'function'
        ? cart.getSectionsToRender().map((section) => section.id)
        : ['cart-icon-bubble'];
      button.disabled = true;
      try {
        const response = await fetch(this.root.dataset.cartAddUrl || '/cart/add.js', {
          method: 'POST',
          headers: { 'content-type': 'application/json', accept: 'application/json' },
          body: JSON.stringify({ id, quantity: 1, sections, sections_url: window.location.pathname })
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload.description || 'Shopify no pudo agregar la carta.');
        }
        if (cart && typeof cart.renderContents === 'function' && payload.sections) {
          try {
            if (typeof cart.setActiveElement === 'function') cart.setActiveElement(button);
            cart.classList.remove('is-empty');
            cart.renderContents(payload);
          } catch (error) {
            console.warn('No pudimos renderizar el carrito del theme; se actualizara el contador.', error);
          }
        }
        updateCartBubble(payload.sections?.['cart-icon-bubble']);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          await publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'singles-api-search',
            cartData: payload,
            variantId: id
          });
        }
        button.textContent = 'Agregada';
        window.setTimeout(() => {
          button.textContent = 'Agregar al carrito';
        }, 1400);
      } catch (error) {
        this.setMessage(error.message || String(error), 'error');
      } finally {
        button.disabled = false;
      }
    }

    filterSearchableFacet(input) {
      const panel = input.closest('.singles-api-search__facet-panel');
      const query = normalize(input.value);
      let visible = 0;
      panel?.querySelectorAll('[data-filter-text]').forEach((item) => {
        const matches = !query || normalize(item.dataset.filterText).includes(query);
        item.hidden = !matches;
        if (matches) visible += 1;
      });
      const empty = panel?.querySelector('[data-facet-empty]');
      if (empty) empty.classList.toggle('hidden', !query || visible > 0);
    }

    facetLabel(filter) {
      if (this.params.get('game') === 'magic' && (
        filter.id === 'magic_format' || filter.paramName === 'filter.p.m.simple_tcg.magic_playable_formats'
      )) return 'Legalidad';
      if (this.isEditionFilter(filter)) {
        return `Edición ${GAME_LABELS[this.params.get('game')] || ''}`.trim();
      }
      const label = filter.label || filter.id;
      const game = this.params.get('game');
      if (game === 'pokemon_en' || game === 'pokemon_jp') {
        const normalizedLabel = normalize(label);
        if (normalizedLabel === 'supertype' || normalizedLabel === 'supertypes') return 'Supertipo';
        if (normalizedLabel === 'subtype' || normalizedLabel === 'subtypes') return 'Subtipo';
      }
      return label;
    }

    isEditionFilter(filter) {
      const id = normalize(filter.id);
      const label = normalize(filter.label);
      const param = normalize(filter.paramName);
      return id === 'set' || id === 'setcode' || label.includes('edicion') || label.includes('edition') || label.includes('set') || param.includes('set');
    }

    isArtistFilter(filter) {
      const id = normalize(filter.id);
      const label = normalize(filter.label);
      const param = normalize(filter.paramName);
      return id === 'artist' || label.includes('artista') || label.includes('artist') || param.includes('artist');
    }

    updateUrl() {
      const next = `${window.location.pathname}?${this.params.toString()}`;
      window.history.replaceState({}, '', next);
    }

    setMessage(message, kind) {
      if (!this.messageTarget) return;
      this.messageTarget.textContent = message;
      this.messageTarget.hidden = !message;
      this.messageTarget.className = 'singles-api-search__status';
      if (kind) this.messageTarget.classList.add(`singles-api-search__status--${kind}`);
    }

    openMobileFilters() {
      this.root.classList.add('is-filter-open');
      this.root.querySelector('[data-mobile-filters-open]')?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('overflow-hidden-mobile');
    }

    closeMobileFilters() {
      this.root.classList.remove('is-filter-open');
      this.root.querySelector('[data-mobile-filters-open]')?.setAttribute('aria-expanded', 'false');
      const releaseScrollLock = () => {
        if (!this.root.classList.contains('is-filter-open')) document.body.classList.remove('overflow-hidden-mobile');
      };
      releaseScrollLock();
      window.requestAnimationFrame(releaseScrollLock);
    }
  }

  function init(root) {
    if (initialized.has(root)) return;
    initialized.add(root);
    new SinglesApiSearchController(root).init();
  }

  function initAll(scope = document) {
    scope.querySelectorAll('[data-singles-api-search]').forEach(init);
  }

  document.addEventListener('DOMContentLoaded', () => initAll());
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
